import {
  createContext,
  useCallback,
  useContext,
  type ElementType,
  type ReactNode,
} from "react"
import en from "@/locales/en.json"
import { cn } from "cn"

import { NO_TRANSLATE } from "@/lib/weglot"
import type { TranslationKey, Translations } from "./types"

// English source translations: the client-side fallback so a value always exists. The one
// locale in the client bundle (others are server-only), so adding a language stays free.
const EN: Translations = en

// Empty default: no shipped translations → the hook reads EN (English source), so an
// unwired render tree is still correct.
const EMPTY: Translations = {}

// Holds the request-resolved translations supplied via SSR props. No Redux: these strings
// are request-scoped, not client state. One flat set — no namespaces.
const TranslationsContext = createContext<Translations>(EMPTY)

// Provide the request's curated translations, app-wide (from WebApp). `value` is optional —
// falls back to empty translations so consumers render the English source from EN.
export function TranslationsProvider({
  value,
  children,
}: {
  value?: Translations
  children: ReactNode
}) {
  return (
    <TranslationsContext.Provider value={value ?? EMPTY}>
      {children}
    </TranslationsContext.Provider>
  )
}

// Values substituted into a translation's `{name}` placeholders (single-brace ICU style,
// matching en.json). A placeholder with no matching var stays visible in the output so the
// gap is caught in review rather than silently blanked.
export type TranslationVars = Record<string, string | number>

// The translator. `t(key, vars?)` returns the SSR-resolved translation, falling back to
// the English source, with `{name}` placeholders filled from `vars`. The server owns
// locale resolution, so the client just trusts the pinned translations — no
// divergence/hydration guard. Rejects unknown keys at compile time.
export type Translator = (key: TranslationKey, vars?: TranslationVars) => string

// Hook for the translator. One call serves every key. `t` is identity-stable across
// renders (it changes only with the provided translations), so it belongs directly in
// effect/callback deps — never mirror it into a ref.
export function useTranslation(): Translator {
  const translations = useContext(TranslationsContext)
  return useCallback(
    (key: TranslationKey, vars?: TranslationVars): string => {
      const text = translations[key] ?? EN[key]
      if (!vars) return text
      return text.replace(/\{(\w+)\}/g, (placeholder, name: string) =>
        name in vars ? String(vars[name]) : placeholder
      )
    },
    [translations]
  )
}

// Render a curated string, always marked `no-translate` so Weglot leaves our
// server-resolved chrome alone (harmless for English — Weglot is a no-op there). `as`
// renders as the target element (no wrapper node).
export function Translate({
  translationKey,
  vars,
  as: Tag = "span",
  className,
}: {
  translationKey: TranslationKey
  vars?: TranslationVars
  as?: ElementType
  className?: string
}) {
  const t = useTranslation()
  return (
    <Tag className={cn(className, NO_TRANSLATE)}>{t(translationKey, vars)}</Tag>
  )
}
